import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { createSelector } from '@ngrx/store';
import { createFeatureSelector } from '@ngrx/store';
import { toDate } from 'app/modules/main/helper/date-mapper';
import { newPublisherAccount } from 'app/modules/main/models/account';
import { newAdvertiserAccount } from 'app/modules/main/models/account';
import { sameAccount } from 'app/modules/main/models/account';
import { isPublisher } from 'app/modules/main/models/account';
import { isAdvertiser } from 'app/modules/main/models/account';
import { Account } from 'app/modules/main/models/account';
import { PublisherAccount } from 'app/modules/main/models/account';
import { AdvertiserAccount } from 'app/modules/main/models/account';
import { AccountIdentity } from 'app/modules/main/models/account';
import { AccountType } from 'app/modules/main/models/account';
import { Authorization } from 'app/modules/main/models/authorization';
import { NowProvider } from 'app/modules/main/services/now-provider.service';
import { UserAuthorizationState } from 'app/modules/main/store/user-authorization/user-authorization.state';
import { combineLatest } from 'rxjs';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { distinctUntilChanged } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';
import { filter } from 'rxjs/operators';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UserAuthorizationSelectors {

  static readonly FEATURE = 'UserAuthorization';

  private readonly feature = createFeatureSelector<UserAuthorizationState>(
    UserAuthorizationSelectors.FEATURE
  );

  private readonly authorization = createSelector(
    this.feature,
    (state: UserAuthorizationState) => state.authorization
  );

  constructor(
    private readonly nowProvider: NowProvider,
    private readonly store: Store<UserAuthorizationState>,
  ) {
  }

  public isAuthorized(): Observable<boolean> {

    return combineLatest([
      this.isAuthorizedForAdvertiser(),
      this.isAuthorizedForPublisher(),
    ])
      .pipe(
        map(([a, b]) => a || b),
        distinctUntilChanged(),
      );
  }

  public isAuthorizedForAdvertiser(
    advertiserId?: number
  ): Observable<boolean> {

    return this.store.select(this.authorization)
      .pipe(
        map(authorization => {
          return !!authorization
            && isAdvertiser(authorization.account)
            && (!advertiserId || advertiserId === authorization.account.accountId)
            && toDate(authorization.expires) > this.nowProvider.now();
        }),
        distinctUntilChanged(),
      );
  }

  public isAuthorizedForPublisher(
    publisherId?: number
  ): Observable<boolean> {

    return this.store.select(this.authorization)
      .pipe(
        map(authorization => {
          return !!authorization
            && isPublisher(authorization.account)
            && (!publisherId || publisherId === authorization.account.accountId)
            && toDate(authorization.expires) > this.nowProvider.now();
        }),
        distinctUntilChanged(),
      );
  }

  public onAuthorized(): Observable<boolean> {

    return this.isAuthorized()
      .pipe(filter(authorized => !!authorized));
  }

  public getAuthorization(): Observable<Authorization<Account>> {

    return this.onAuthorized()
      .pipe(switchMap(() => this.store.select(this.authorization)));
  }

  public getAdvertiserAuthorization(): Observable<Authorization<AdvertiserAccount>> {

    return this.getAuthorization()
      .pipe(
        filter(authorization => isAdvertiser(authorization.account)),
        map(authorization => this.toAdvertiserAccountAuthorization(authorization)),
      );
  }

  public getPublisherAuthorization(): Observable<Authorization<PublisherAccount>> {

    return this.getAuthorization()
      .pipe(
        filter(authorization => isPublisher(authorization.account)),
        map(authorization => this.toPublisherAccountAuthorization(authorization)),
      );
  }

  public getAccount(): Observable<Account> {

    return this.onAuthorized()
      .pipe(
        switchMap(() => this.store.select(this.authorization)),
        map(authorization => authorization.account),
        distinctUntilChanged(sameAccount),
        map((account: AccountIdentity) => {

          switch (account.accountType) {

            case AccountType.ADVERTISER:
              return this.toAdvertiserAccount(account);

            case AccountType.PUBLISHER:
              return this.toPublisherAccount(account);

            default:
              throw new Error('Account type not recognised');
          }
        }),
      );
  }

  public getAdvertiserAccount(): Observable<AdvertiserAccount> {

    return this.getAccount()
      .pipe(
        filter(account => isAdvertiser(account)),
        map(account => account as AdvertiserAccount),
      );
  }

  public getPublisherAccount(): Observable<PublisherAccount> {

    return this.getAccount()
      .pipe(
        filter(account => isPublisher(account)),
        map(account => account as PublisherAccount),
      );
  }

  public getAdvertiserId(): Observable<number> {

    return this.getAdvertiserAccount()
      .pipe(map(account => account.accountId));
  }

  public getPublisherId(): Observable<number> {

    return this.getPublisherAccount()
      .pipe(map(account => account.accountId));
  }

  public getOneAdvertiserId(): Observable<number> {

    return this.getAdvertiserId()
      .pipe(first());
  }

  public getOnePublisherId(): Observable<number> {

    return this.getPublisherId()
      .pipe(first());
  }

  private toAdvertiserAccountAuthorization(
    authorization: Authorization<Account>
  ): Authorization<AdvertiserAccount> {

    return {
      ...authorization,
      account: this.toAdvertiserAccount(authorization.account)
    };
  }

  private toAdvertiserAccount(
    account: AccountIdentity
  ): AdvertiserAccount {
    return newAdvertiserAccount(account.accountId);
  }

  private toPublisherAccountAuthorization(
    authorization: Authorization<Account>
  ): Authorization<PublisherAccount> {

    return {
      ...authorization,
      account: this.toPublisherAccount(authorization.account)
    };
  }

  private toPublisherAccount(
    account: AccountIdentity
  ): PublisherAccount {
    return newPublisherAccount(account.accountId);
  }

}
